import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector, useAppStore } from "../redux/hooks";
import { addSavedJob, removeSavedJob } from "../redux/features/jobs/jobsSlice";
import {
  useDeleteUserStateItemMutation,
  useUpdateUserStateItemMutation,
} from "../redux/services/userStateApi";
import type { UserStateEntry } from "../types/user-state";
import { useAuth } from "./useAuth";
import { setEntry, removeEntry } from "../redux/features/userState/userStateSlice";
import { openSignInPrompt } from "../redux/features/authPrompt/authPromptSlice";
import { shouldOpenSignInPromptAfterGuestSave } from "../utils/engagement/sign-in-save-nudge-session";
import { buildSavedJobUserStatePayload } from "../utils/user-state/build-job-user-state-payload";
import { normalizeUserStateVersion } from "../utils/user-state/normalize-user-state-version";
import { captureSavedJobUserStateSyncFailure } from "../utils/user-state/capture-saved-job-user-state-sync-failure";

type SavableJob = {
  objectID?: string;
  id?: string;
  [key: string]: unknown;
};

export function useSavedJobActions() {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const userStateEntries = useAppSelector((state) => state.userState?.entries ?? {});
  const savedJobs = useAppSelector((state) => state.jobs?.savedJobs ?? []);
  const [updateUserStateItem] = useUpdateUserStateItemMutation();
  const [deleteUserStateItem] = useDeleteUserStateItemMutation();
  const { isAuthenticated } = useAuth();

  const savedJobIndex = useMemo(() => {
    return new Map(savedJobs.map((job) => [job.id, job]));
  }, [savedJobs]);

  const saveJob = useCallback(
    async (job: SavableJob): Promise<boolean> => {
      const jobId = String(job.objectID ?? job.id ?? "");
      if (!jobId) {
        console.warn("[user-state] Tried to save job without id");
        return false;
      }

      const normalizedJob = { ...job, id: jobId };
      const savedAt = new Date().toISOString();
      const persistPayload = buildSavedJobUserStatePayload(normalizedJob, savedAt);

      // Always update local Redux state (which is persisted via redux-persist)
      dispatch(addSavedJob(normalizedJob));

      // Validate local state was updated
      const state = store.getState();
      const isInSavedJobs = state.jobs.savedJobs.some((j) => j.id === jobId);
      if (!isInSavedJobs) {
        console.error("[user-state] Failed to save job locally");
        return false;
      }

      // Only sync to remote if authenticated
      if (!isAuthenticated) {
        if (shouldOpenSignInPromptAfterGuestSave()) {
          dispatch(openSignInPrompt());
        }
        return true; // Success for local-only operation
      }

      const itemKey = getSavedJobItemKey(jobId);
      const currentEntry = userStateEntries[itemKey] as UserStateEntry | undefined;
      const version = normalizeUserStateVersion(currentEntry?.version);

      try {
        const result = await updateUserStateItem({
          itemKey,
          data: persistPayload,
          version,
        }).unwrap();
        // Update local Redux userState to keep it in sync with backend
        if (result) {
          dispatch(setEntry({ itemKey, entry: result }));
        }
        return true; // Success
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("[user-state] Failed to persist saved job", error);
        }
        captureSavedJobUserStateSyncFailure({
          operation: "persist",
          itemKey,
          jobId,
          clientVersion: version,
          error,
        });
        const conflictEntry = extractConflictEntry(error);
        if (conflictEntry) {
          // Handle conflict - update local state with server version
          dispatch(setEntry({ itemKey, entry: conflictEntry }));
          const serverJob = { id: jobId, ...conflictEntry.data };
          dispatch(removeSavedJob(jobId));
          dispatch(addSavedJob(serverJob as SavableJob));
          // Validate the job is still saved after conflict resolution
          const updatedState = store.getState();
          const stillSaved = updatedState.jobs.savedJobs.some((j) => j.id === jobId);
          return stillSaved;
        } else {
          dispatch(removeSavedJob(jobId));
          return false; // Failed
        }
      }
    },
    [dispatch, store, updateUserStateItem, userStateEntries, isAuthenticated]
  );

  const unsaveJob = useCallback(
    async (job: SavableJob | string): Promise<boolean> => {
      const jobId = typeof job === "string" ? job : String(job.objectID ?? job.id ?? "");
      if (!jobId) {
        console.warn("[user-state] Tried to unsave job without id");
        return false;
      }

      const fallbackJob =
        typeof job === "string" ? savedJobIndex.get(job) ?? { id: jobId } : { ...job, id: jobId };

      // Always update local Redux state (which is persisted via redux-persist)
      dispatch(removeSavedJob(jobId));

      // Validate local state was updated
      const state = store.getState();
      const isRemoved = !state.jobs.savedJobs.some((j) => j.id === jobId);
      if (!isRemoved) {
        console.error("[user-state] Failed to unsave job locally");
        return false;
      }

      // Only sync to remote if authenticated
      if (!isAuthenticated) {
        return true; // Success for local-only operation
      }

      const itemKey = getSavedJobItemKey(jobId);
      const currentEntry = userStateEntries[itemKey] as UserStateEntry | undefined;
      const version = normalizeUserStateVersion(currentEntry?.version);

      try {
        await deleteUserStateItem({
          itemKey,
          version: version > 0 ? version : undefined,
        }).unwrap();
        // Remove from local Redux userState to keep it in sync with backend
        dispatch(removeEntry(itemKey));
        return true; // Success
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("[user-state] Failed to delete saved job", error);
        }
        captureSavedJobUserStateSyncFailure({
          operation: "delete_remote",
          itemKey,
          jobId,
          clientVersion: version,
          error,
        });
        const conflictEntry = extractConflictEntry(error);
        if (conflictEntry) {
          // Conflict means item was updated on server - restore it
          dispatch(setEntry({ itemKey, entry: conflictEntry }));
          dispatch(addSavedJob({ id: jobId, ...conflictEntry.data }));
          // Validate the job was restored after conflict
          const updatedState = store.getState();
          const wasRestored = updatedState.jobs.savedJobs.some((j) => j.id === jobId);
          return !wasRestored; // Return false if job was restored (unsave failed)
        } else if (fallbackJob) {
          dispatch(addSavedJob(fallbackJob as SavableJob));
          return false; // Failed to unsave
        }
        return false; // Failed
      }
    },
    [dispatch, store, deleteUserStateItem, savedJobIndex, userStateEntries, isAuthenticated]
  );

  return { saveJob, unsaveJob };
}

function getSavedJobItemKey(jobId: string): `savedJob#${string}` {
  return `savedJob#${jobId}`;
}

function extractConflictEntry(error: unknown): UserStateEntry | null {
  if (!error || typeof error !== "object") {
    return null;
  }

  const maybeError = error as { status?: number; data?: unknown; error?: unknown };

  if ("data" in maybeError && "status" in maybeError && maybeError.status === 409) {
    const entry = maybeError.data as UserStateEntry | undefined;
    return entry ?? null;
  }

  if ("error" in maybeError && maybeError.error && typeof maybeError.error === "object") {
    const inner = maybeError.error as { status?: number; data?: unknown };
    if (inner.status === 409) {
      return inner.data as UserStateEntry;
    }
  }

  return null;
}

